import React, { useState } from 'react';
import AddEditActivity from '../../../components/AddEditActivity';
import { created_activity_id } from '../../../components/AddEditActivity/activity_helpers';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import general_fetch from '../../../utils/fetch';
import { TaskBtn } from './task_ui';
import { activity_prefill_from_task } from './task_ticket_links';

/**
 * "Log call / meeting" on a task card (refinement B, 2026-09-12): Activities are
 * the record of contact, a Task is the thing to do — so the rep logs the call /
 * meeting she did FOR the task from the task itself. Opens the native
 * AddEditActivity (add mode) prefilled — org = this client, type = call
 * (AddEditActivity's default), subject = the task title, note names the task —
 * via the sessionStorage 'cs_task_prefill' channel the modal already reads.
 * When the activity is created, AddEditActivity hands the saved row to
 * on_created and we link it to the task through the existing link-activity
 * route, then tell the Linked work panel to refresh.
 *
 * AddEditActivity needs the logged-in user, the CS user roster (assignee
 * picker) and the org (name + users). The per-client Tasks tab passes them
 * down from StoreDashboard; where they aren't supplied (the portfolio page
 * mounts the same TaskCard) they're fetched lazily on first click and cached
 * for the session.
 */
const ctx_cache = { user_details: null, cs_org_users: null, orgs: {} };

const resolve_context = async (store_id, given) => {
    if (given.user_details && given.user_details.id) ctx_cache.user_details = given.user_details;
    if (!ctx_cache.user_details) {
        const u = await general_fetch({ url: 'user/get_details', data_field: 'user' });
        ctx_cache.user_details = (u && u.user_details) || null;
    }
    if (!ctx_cache.cs_org_users) {
        const users = await general_fetch({ url: 'user/get_all' });
        ctx_cache.cs_org_users = Array.isArray(users) ? users : [];
    }
    if (given.store_info && given.store_info.name) ctx_cache.orgs[store_id] = given.store_info;
    if (!ctx_cache.orgs[store_id]) {
        const org = await general_fetch({ url: 'csp/get_org_info', body: { customer_store_id: store_id } });
        ctx_cache.orgs[store_id] = org && typeof org === 'object' ? { id: store_id, ...org } : { id: store_id, name: store_id };
    }
    const org = ctx_cache.orgs[store_id];
    return {
        user_details: ctx_cache.user_details,
        cs_org_users: ctx_cache.cs_org_users,
        org: org.id ? org : { id: store_id, ...org },
        // One stable array: AddEditActivity copies current_org_users into state
        // in an effect keyed on the prop reference.
        org_users: Array.isArray(org.all_users) ? [...org.all_users] : [],
    };
};

const TaskLogActivity = ({ store_id, task, user_details, store_info, set_page_loader, on_linked, disabled = false }) => {
    const [open, set_open] = useState(false);
    const [ctx, set_ctx] = useState(null);
    const [busy, set_busy] = useState(false);
    const [note, set_note] = useState(null); // {kind:'ok'|'err', text}

    const start = async () => {
        if (busy) return;
        set_busy(true);
        set_note(null);
        try {
            const c = await resolve_context(store_id, { user_details, store_info });
            if (!c.user_details || !c.user_details.id) throw new Error('Could not resolve the logged-in user');
            const prefill = activity_prefill_from_task(task, store_id);
            try { sessionStorage.setItem('cs_task_prefill', JSON.stringify(prefill)); } catch (_e) { /* modal opens blank */ }
            set_ctx(c);
            set_open(true);
        } catch (e) {
            set_note({ kind: 'err', text: (e && e.message) || (e && e.err && e.err.message) || 'Could not open the activity form' });
        } finally {
            set_busy(false);
        }
    };

    const on_created = async (resp) => {
        const activity_id = created_activity_id(resp);
        if (!activity_id) {
            set_note({ kind: 'err', text: 'Activity saved, but its id was not returned — link it from Linked work.' });
            return;
        }
        try {
            await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables/${task.id}/link-activity`,
                method: 'POST',
                body: { activityId: String(activity_id) },
            });
            set_note({ kind: 'ok', text: 'Activity logged and linked to this task.' });
            if (on_linked) on_linked(activity_id);
        } catch (e) {
            set_note({ kind: 'err', text: 'Activity saved, but linking failed: ' + ((e && e.message) || String(e)) });
        }
    };

    const loader = typeof set_page_loader === 'function' ? set_page_loader : () => {};

    return (
        <>
            <TaskBtn
                variant='neutral'
                disabled={disabled || busy}
                title='Log the call / meeting you did for this task (creates an Activity and links it here)'
                onClick={start}
            >
                {busy ? '…' : '☎ Log call / meeting'}
            </TaskBtn>
            {note ? (
                <span style={{ fontSize: 11, color: note.kind === 'ok' ? '#1e8f5b' : '#c0392b', marginLeft: 4 }}>{note.text}</span>
            ) : null}
            {ctx ? (
                <AddEditActivity
                    open={open}
                    toggle={() => set_open(false)}
                    mode='add'
                    active_activity={null}
                    cs_org_users={ctx.cs_org_users}
                    user_details={ctx.user_details}
                    get_activities={() => {}}
                    all_orgs={[]}
                    set_page_loader={loader}
                    current_org_users={ctx.org_users}
                    current_org={ctx.org}
                    open_create_activity_modal={() => {}}
                    cs_executive_id_of_org={ctx.org && ctx.org.customer_success_executive_id}
                    on_created={on_created}
                />
            ) : null}
        </>
    );
};

export default TaskLogActivity;
